import React, {useEffect, useState} from 'react';
import {NavLink} from 'react-router-dom';
import config from '../../../../../../../config';
import Translate from '../../../../../../../components/Translate';
import Input from '../../../../../../../components-new/form/Input';
import BlueButton from '../../../../../../../components-new/buttons/BlueButton';
import FileUpload from '../../../../../../../components-new/general/FileUpload';
import GraphIcon from '../../../../../../../assets/icons/GraphIcon';
import WebchatIcon from '../../../../../../../assets/icons/platforms/WebchatIcon';
import WebchatMobileIcon from '../../../../../../../assets/icons/platforms/WebchatMobileIcon';
import {updateClientName} from '../../../../../../../services/company/api';
import '../../../../../../../styles/settings-layout.css';
import './BotOverview.css';
import MessagingSettings from './MessagingSettings';

/*
 * The bot's own page - the first thing open when a bot is selected.
 *
 * It held two or three round icon buttons in the middle of an empty panel, each
 * needing a tooltip before anyone could tell what it did, and nothing else. The
 * bot's identity lived somewhere else entirely: its picture and its name were
 * editable only from the row in the sidebar, where the picture had to be hovered
 * to reveal that it could be changed at all and the name was behind a menu.
 *
 * All of it is here now, in the cards the settings screens are built from.
 */
const BotOverview = ({
                         client,
                         isScenario,
                         onOpenChat,
                         onOpenMobileChat,
                         onImageChange,
                         refreshCompany,
                         addNotification
                     }) => {
    const [name, setName] = useState(client.name || '');
    const [saving, setSaving] = useState(false);
    const [messaging, setMessaging] = useState(null);
    const testingDisabled = !messaging || messaging.companyId !== client.id || !messaging.enabled;
    /*
     * What the server was last told. The button used to compare against the name
     * in the store, which only changes once the company has been fetched back -
     * so it stayed on screen for the whole round trip after the save had already
     * succeeded.
     */
    const [savedName, setSavedName] = useState(client.name || '');

    // a different bot was picked while this page was open
    useEffect(() => {
        setName(client.name || '');
        setSavedName(client.name || '');
    }, [client.id, client.name]);

    const trimmed = name.trim();
    const nameChanged = !!trimmed && trimmed !== savedName;

    const saveName = () => {
        if (!nameChanged || saving) return;

        setSaving(true);
        updateClientName(client.id, trimmed)
            .then(() => {
                setSavedName(trimmed);
                addNotification('successful', config.notificationLevels.success);
                /*
                 * A plain refetch, and it has to be one.
                 *
                 * This used to call onImageChange to make the store reload - the
                 * name is read from there by the sidebar, the chat list and the
                 * header. But that handler sends the whole client back to the
                 * server, built out of the copy in the store, which at this moment
                 * still holds the old name: a second request went out and put the
                 * old name straight back.
                 */
                if (refreshCompany) refreshCompany();
            })
            .catch(() => {
                addNotification('somethingWentWrong', config.notificationLevels.error);
                setName(savedName);
            })
            .finally(() => setSaving(false));
    };

    /*
     * The animation is keyed on the bot's id, so picking another one replays it.
     * Switching used to change the contents of the panel with nothing moving,
     * and on two bots with similar names it was not obvious anything had.
     */
    return (
        <div className="stg-page bot-overview" key={client.id}>

            <MessagingSettings key={client.id} companyId={client.id} addNotification={addNotification}
                               onStateChange={setMessaging}/>

            <div className="stg-card">
                <div className="bot-overview__identity">
                    <div className="bot-overview__photo">
                        <FileUpload
                            windowSize={72}
                            imgSize={60}
                            progressSize={72}
                            handleChange={onImageChange}
                            handleDelete={() => onImageChange(client, '')}
                            client={client}
                            image={client.image}
                        />
                    </div>

                    <div className="bot-overview__naming">
                        <span className="bot-overview__label"><Translate text="botName"/></span>
                        <div className="bot-overview__name-row">
                            <Input
                                type="text"
                                value={name}
                                onChange={(e) => {
                                    if (e.target.value.length < 100) setName(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveName();
                                    if (e.key === 'Escape') setName(client.name || '');
                                }}
                            />
                            {/* only once there is something to save: a button that
                                is always live invites a pointless request */}
                            {nameChanged && (
                                <BlueButton width="auto" height="40px" loading={saving} onClick={saveName}>
                                    <Translate text="save"/>
                                </BlueButton>
                            )}
                        </div>
                    </div>

                    <span className="bot-overview__kind">
                        <Translate text={isScenario ? 'scenario' : 'smart'}/>
                    </span>
                </div>

                <p className="stg-card__note bot-overview__note">
                    <Translate text={isScenario ? 'botOverviewScenarioNote' : 'botOverviewSmartNote'}/>
                </p>
            </div>


            <div className="stg-card">
                <div className="stg-card__head">
                    <h3 className="stg-card__title"><Translate text="botOverviewWork"/></h3>
                </div>

                <div className="bot-overview__actions">
                    {isScenario && (
                        <NavLink className="bot-overview__action" to={`/dashboard/graph/${client.id}`}>
                            <span className="bot-overview__action-icon">
                                <GraphIcon width={20} height={20} stroke="currentColor" strokeWidth={1.5}/>
                            </span>
                            <span className="bot-overview__action-text">
                                <span className="bot-overview__action-title"><Translate text="treeBuild"/></span>
                                <span className="bot-overview__action-note"><Translate text="botOverviewTreeNote"/></span>
                            </span>
                        </NavLink>
                    )}

                    <button type="button" className="bot-overview__action" onClick={onOpenChat}
                            disabled={testingDisabled}>
                        <span className="bot-overview__action-icon">
                            <WebchatIcon width={20} height={20} stroke="currentColor" strokeWidth={1.5}/>
                        </span>
                        <span className="bot-overview__action-text">
                            <span className="bot-overview__action-title"><Translate text="webchatTesting"/></span>
                            <span className="bot-overview__action-note"><Translate text="botOverviewTestNote"/></span>
                        </span>
                    </button>

                    <button type="button" className="bot-overview__action" onClick={onOpenMobileChat}
                            disabled={testingDisabled}>
                        <span className="bot-overview__action-icon">
                            <WebchatMobileIcon width={20} height={20} stroke="currentColor" strokeWidth={1.5}/>
                        </span>
                        <span className="bot-overview__action-text">
                            <span className="bot-overview__action-title"><Translate text="webchatTestingForMobile"/></span>
                            <span className="bot-overview__action-note"><Translate text="botOverviewTestMobileNote"/></span>
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BotOverview;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/overview/BotOverview.jsx